import { useState, useRef } from "react";
import { useNavigate } from "react-router";
import * as XLSX from "xlsx";
import { useTranslation } from "../../../i18n/TranslationProvider";

const formHeader = [
    "first_name",
    "last_name",
    "email",
    "dob",
    "school",
    "sport",
    "gender",
    "position",
    "previous_concussion_count",
    "phone",
    "grad_year",
];

const TemplateRosterUpload = ({ userId }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);
    const { t } = useTranslation();

    const processFile = (file) => {
        setLoading(true);
        setError("");

        const reader = new FileReader();
        reader.onerror = () => {
            setLoading(false);
            setError(t("athletes.failedToReadFile"));
        };

        reader.onload = (evt) => {
            try {
                const data = evt.target.result;
                if (!data) throw new Error("Empty file");

                const workbook = XLSX.read(data, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];

                // 2D array: header row + data rows
                const rows = XLSX.utils.sheet_to_json(sheet, {
                    header: 1,
                    defval: "",
                    raw: false,
                });

                const dataRows = rows.slice(5);

                const formattedData = dataRows.map((row) => {
                    const obj = {};
                    formHeader.forEach((key, idx) => {
                        obj[key] = row[idx] ?? "";
                    });
                    return obj;
                });

                navigate("/add-new-athletes/review", {
                    state: { parsedData: formattedData, userId: userId },
                });
            } catch (err) {
                console.error(err);
                setError(t("athletes.templateParseError"));
            } finally {
                setLoading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return setError(t("athletes.noFileSelectedError"));
        processFile(file);
    };

    const onDragEnter = (e) => {
        e.preventDefault();
        setDragActive(true);
    };
    const onDragOver = (e) => {
        e.preventDefault();
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        setDragActive(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    return (
        <>
            {!loading ? (
                <div
                    onDragEnter={onDragEnter}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    <label
                        htmlFor="file-upload"
                        className={`relative flex flex-col items-center justify-center
              border-2 border-dashed rounded-lg p-8 cursor-pointer text-center
              ${dragActive ? "border-primary bg-gray-300" : "border-gray-300"}`}
                    >
                        <input
                            ref={fileInputRef}
                            id="file-upload"
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleFileUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-10"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                            />
                        </svg>

                        <p className="text-gray-600">
                            {t("athletes.dragDropTemplateHere")}
                            <br />
                            <span className="underline text-primary">
                                {t("athletes.orClickToBrowse")}
                            </span>
                        </p>
                    </label>
                </div>
            ) : (
                <h6>{t("common.loading")}</h6>
            )}
            <p className="text-red-600">{error}</p>
        </>
    );
};

export default TemplateRosterUpload;